import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createPublicacion, PublicacionData } from '../../services/blogPublicaciones.ts';
import { fetchCategorias, Categoria } from '../../services/blogCategorias.ts';
import { convertImageToWebP } from '../../utils/imageCompressor.ts';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface AddBlogPublicacionesProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AddBlogPublicaciones: React.FC<AddBlogPublicacionesProps> = ({ isOpen, onClose, onSuccess }) => {
    const [titulo, setTitulo] = useState('');
    const [subtitulo, setSubtitulo] = useState('');
    const [contenido, setContenido] = useState('');
    const [categoriaId, setCategoriaId] = useState<number | null>(null);
    const [esDestacado, setEsDestacado] = useState(false);
    const [estado, setEstado] = useState<'borrador' | 'publicado'>('borrador');
    const [fechaPublicacion, setFechaPublicacion] = useState('');
    const [imagen, setImagen] = useState<File | null>(null);
    const [imagenPreview, setImagenPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [cargandoCategorias, setCargandoCategorias] = useState(false);

    // Cargar categorías para el select
    useEffect(() => {
        if (isOpen) {
            const loadCategorias = async () => {
                setCargandoCategorias(true);
                try {
                    const data = await fetchCategorias();
                    setCategorias(data);
                } catch (err) {
                    /* Browser diagnostics omitted. */
                } finally {
                    setCargandoCategorias(false);
                }
            };
            loadCategorias();
        }
    }, [isOpen]);

    const handleImagenChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const webpFile = await convertImageToWebP(file);
            setImagen(webpFile);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagenPreview(reader.result as string);
            };
            reader.readAsDataURL(webpFile);
        } else {
            setImagen(null);
            setImagenPreview(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!titulo.trim()) {
            setError('El título es obligatorio');
            return;
        }
        if (!contenido.trim()) {
            setError('El contenido es obligatorio');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data: PublicacionData = {
                titulo: titulo.trim(),
                subtitulo: subtitulo.trim() || null,
                contenido: contenido.trim(),
                categoria_id: categoriaId,
                es_destacado: esDestacado,
                estado: estado,
                fecha_publicacion: fechaPublicacion || null,
                imagen: imagen
            };
            await createPublicacion(data);
            onSuccess();
            onClose();
            // resetear formulario
            setTitulo('');
            setSubtitulo('');
            setContenido('');
            setCategoriaId(null);
            setEsDestacado(false);
            setEstado('borrador');
            setFechaPublicacion('');
            setImagen(null);
            setImagenPreview(null);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.message || 'Error al crear la publicación');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b px-6 py-4 sticky top-0 bg-white z-10">
                    <LumaAccessibleTitle className="text-lg font-semibold text-gray-900">Agregar Publicación de Blog</LumaAccessibleTitle>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={handleSubmit}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div className="px-6 py-4 space-y-4">
                        {error && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                                {error}
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Título *
                            </label>
                            <input
                                type="text"
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Subtítulo
                            </label>
                            <input
                                type="text"
                                value={subtitulo}
                                onChange={(e) => setSubtitulo(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Contenido *
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={contenido}
                                onChange={setContenido}
                                placeholder="Escribe el contenido aquí..."
                                className="h-64 mb-12" // ajusta la altura y margen según tu diseño
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Categoría
                            </label>
                            <select
                                value={categoriaId ?? ''}
                                onChange={(e) => setCategoriaId(e.target.value ? Number(e.target.value) : null)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={cargandoCategorias}
                            >
                                <option value="">Seleccione una categoría</option>
                                {categorias.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Imagen destacada
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImagenChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                            {imagenPreview && (
                                <div className="mt-2">
                                    <img src={imagenPreview} alt="Vista previa" className="max-h-32 rounded-md" />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={esDestacado}
                                    onChange={(e) => setEsDestacado(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Destacar publicación</span>
                            </label>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Estado
                                </label>
                                <select
                                    value={estado}
                                    onChange={(e) => setEstado(e.target.value as 'borrador' | 'publicado')}
                                    className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="borrador">Borrador</option>
                                    <option value="publicado">Publicado</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fecha de publicación
                            </label>
                            <input
                                type="datetime-local"
                                value={fechaPublicacion}
                                onChange={(e) => setFechaPublicacion(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 border-t px-6 py-4 sticky bottom-0 bg-white">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-[#3564db] text-white rounded-md hover:bg-[#8f1f64] disabled:opacity-50"
                        >
                            {loading ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

export default AddBlogPublicaciones;
